import { createContext, useContext, useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';

export type Crumb = { label: string; to?: string };

// The setter and the value live in separate contexts: pages only need the (stable) setter,
// so declaring breadcrumbs never re-renders the page itself.
const SetCrumbsContext = createContext<Dispatch<SetStateAction<Crumb[]>> | null>(null);
const CrumbsContext = createContext<Crumb[]>([]);

export function BreadcrumbsProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  return (
    <SetCrumbsContext.Provider value={setCrumbs}>
      <CrumbsContext.Provider value={crumbs}>{children}</CrumbsContext.Provider>
    </SetCrumbsContext.Provider>
  );
}

/** A page declares its breadcrumbs; the last one is the current page. */
export function useBreadcrumbs(crumbs: Crumb[]): void {
  const setCrumbs = useContext(SetCrumbsContext);
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    setCrumbs?.((prev) => (JSON.stringify(prev) === key ? prev : (JSON.parse(key) as Crumb[])));
  }, [setCrumbs, key]);
}

export function useCurrentCrumbs(): Crumb[] {
  return useContext(CrumbsContext);
}
